import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Send, Paperclip, FileText, Image as ImageIcon, Film, Download } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pendente" },
  { value: "in_progress", label: "Em curso" },
  { value: "done", label: "Concluída" },
];

export default function TaskDialog({ open, onOpenChange, task, clients, defaultClientId, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEditing = !!task;

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mentionState, setMentionState] = useState({ open: false, query: "", start: 0 });

  const loadComments = async (taskId) => {
    try {
      const r = await api.get(`/tasks/${taskId}/comments`);
      setComments(r.data);
    } catch { setComments([]); }
  };

  const postComment = async () => {
    if (!task) return;
    const content = newComment.trim();
    if (!content) return;
    setPostingComment(true);
    try {
      const mentions = resolveMentions(content, team);
      await api.post(`/tasks/${task.id}/comments`, { content, mentions });
      setNewComment("");
      setMentionState({ open: false, query: "", start: 0 });
      loadComments(task.id);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao publicar comentário");
    } finally {
      setPostingComment(false);
    }
  };

  const removeComment = async (c) => {
    try {
      await api.delete(`/tasks/${task.id}/comments/${c.id}`);
      loadComments(task.id);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao eliminar comentário");
    }
  };

  const loadAttachments = async (taskId) => {
    try {
      const r = await api.get(`/tasks/${taskId}/attachments`);
      setAttachments(r.data);
    } catch { setAttachments([]); }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !task) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tasks/${task.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Anexo carregado");
      loadAttachments(task.id);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar anexo");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Eliminar ${att.filename}?`)) return;
    try {
      await api.delete(`/tasks/${task.id}/attachments/${att.id}`);
      loadAttachments(task.id);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao eliminar anexo");
    }
  };

  // ---- @mentions in the comment textarea --------------------------------
  const onCommentChange = (e) => {
    const v = e.target.value;
    setNewComment(v);
    // Look for the last '@' before the caret that starts a token (no spaces after)
    const caret = e.target.selectionStart ?? v.length;
    const upToCaret = v.slice(0, caret);
    const at = upToCaret.lastIndexOf("@");
    if (at === -1) { setMentionState({ open: false, query: "", start: 0 }); return; }
    const prev = at === 0 ? " " : upToCaret[at - 1];
    if (prev && !/\s/.test(prev)) { setMentionState({ open: false, query: "", start: 0 }); return; }
    const q = upToCaret.slice(at + 1);
    if (/\s/.test(q)) { setMentionState({ open: false, query: "", start: 0 }); return; }
    setMentionState({ open: true, query: q, start: at });
  };

  const pickMention = (member) => {
    const before = newComment.slice(0, mentionState.start);
    const rest = newComment.slice(mentionState.start + 1 + mentionState.query.length);
    const inserted = `@${member.name} `;
    setNewComment(before + inserted + rest);
    setMentionState({ open: false, query: "", start: 0 });
  };

  const resolveMentions = (text, teamList) => {
    const ids = new Set();
    const seenNames = new Set();
    const rx = /@([\p{L}][\p{L}0-9 .-]{1,60}?)(?=\s|$|[,;:!?])/gu;
    let match;
    while ((match = rx.exec(text)) !== null) {
      const name = match[1].trim();
      if (!name || seenNames.has(name.toLowerCase())) continue;
      seenNames.add(name.toLowerCase());
      const hit = teamList.find((m) => (m.name || "").toLowerCase() === name.toLowerCase());
      if (hit && hit.id !== user?.id) ids.add(hit.id);
    }
    return Array.from(ids);
  };

  const [form, setForm] = useState({
    client_id: "",
    title: "",
    description: "",
    assignee_ids: [],
    start_date: "",
    end_date: "",
    status: "pending",
    recurrence: "none",
    recurrence_interval: 2,
    recurrence_until: "",
  });
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        client_id: task.client_id,
        title: task.title,
        description: task.description || "",
        assignee_ids: task.assignee_ids && task.assignee_ids.length ? task.assignee_ids : (task.assignee_id ? [task.assignee_id] : []),
        start_date: task.start_date,
        end_date: task.end_date,
        status: task.status,
        recurrence: "none",
        recurrence_interval: 2,
        recurrence_until: "",
      });
      loadComments(task.id);
      loadAttachments(task.id);
    } else {
      const today = new Date().toISOString().slice(0, 10);
      setForm({
        client_id: defaultClientId || clients?.[0]?.id || "",
        title: "",
        description: "",
        assignee_ids: user?.id ? [user.id] : [],
        start_date: today,
        end_date: today,
        status: "pending",
        recurrence: "none",
        recurrence_interval: 2,
        recurrence_until: "",
      });
      setComments([]);
      setAttachments([]);
      setNewComment("");
      setMentionState({ open: false, query: "", start: 0 });
    }
  }, [open, task, defaultClientId, clients, user]);

  useEffect(() => {
    if (!form.client_id) { setTeam([]); return; }
    api.get("/tasks/team", { params: { client_id: form.client_id } })
      .then((r) => setTeam(r.data))
      .catch(() => setTeam([]));
  }, [form.client_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Título obrigatório"); return; }
    if (!form.client_id) { toast.error("Escolhe um cliente"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Datas obrigatórias"); return; }
    if (form.end_date < form.start_date) { toast.error("Data fim antes da data início"); return; }
    if (!form.assignee_ids || form.assignee_ids.length === 0) { toast.error("Indica pelo menos um responsável"); return; }
    setSaving(true);
    try {
      if (isEditing) {
        const payload = {
          title: form.title,
          description: form.description,
          start_date: form.start_date,
          end_date: form.end_date,
          status: form.status,
        };
        if (isAdmin) payload.assignee_ids = form.assignee_ids;
        await api.put(`/tasks/${task.id}`, payload);
        toast.success("Tarefa atualizada");
      } else {
        const payload = {
          client_id: form.client_id,
          title: form.title,
          description: form.description,
          assignee_ids: isAdmin ? form.assignee_ids : [user.id],
          start_date: form.start_date,
          end_date: form.end_date,
          status: form.status,
          recurrence: form.recurrence,
        };
        if (form.recurrence !== "none") {
          if (!form.recurrence_until) { toast.error("Indica a data limite da recorrência"); setSaving(false); return; }
          payload.recurrence_until = form.recurrence_until;
          if (form.recurrence === "custom") {
            const n = parseInt(form.recurrence_interval, 10);
            if (!n || n < 1) { toast.error("Intervalo personalizado inválido"); setSaving(false); return; }
            payload.recurrence_interval = n;
          }
        }
        const r = await api.post("/tasks", payload);
        const created = r.data?.created || 1;
        toast.success(created > 1 ? `${created} tarefas criadas` : "Tarefa criada");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F9F6F0] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#1A1412]">
            {isEditing ? "Editar tarefa" : "Nova tarefa"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#5C4A43]">
            {isEditing
              ? "Atualiza os dados da tarefa. As alterações aplicam-se apenas a esta ocorrência."
              : "Preenche os dados. Podes tornar a tarefa recorrente para gerar várias ocorrências de uma só vez."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!isEditing && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">Cliente</Label>
              <select
                value={form.client_id}
                onChange={(e) => set("client_id", e.target.value)}
                className="w-full mt-1 h-11 px-3 rounded-md border border-[#EAE3DB] bg-white text-sm text-[#1A1412] focus:outline-none focus:ring-2 focus:ring-[#8B5E3C]"
                data-testid="task-client-select"
              >
                {(clients || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">Título</Label>
            <Input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              className="mt-1 h-11 bg-white"
              data-testid="task-title-input"
              placeholder="Ex: Definir estratégia de Instagram"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">Descrição</Label>
            <Textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              className="mt-1 bg-white min-h-[80px]"
              data-testid="task-description-input"
              placeholder="Detalhes da tarefa (opcional)"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">Data início</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                className="mt-1 h-11 bg-white"
                data-testid="task-start-date"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">Data fim</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => set("end_date", e.target.value)}
                className="mt-1 h-11 bg-white"
                data-testid="task-end-date"
              />
            </div>
          </div>

          {isAdmin && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">Responsáveis</Label>
              <div className="mt-1 rounded-md border border-[#EAE3DB] bg-white p-2 max-h-40 overflow-y-auto space-y-1" data-testid="task-assignees-list">
                {team.length === 0 ? (
                  <div className="text-xs text-[#8B5E3C] italic px-2 py-1">Sem membros disponíveis para este cliente.</div>
                ) : (
                  team.map((m) => {
                    const checked = (form.assignee_ids || []).includes(m.id);
                    return (
                      <label key={m.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${checked ? "bg-[#EAE3DB]" : "hover:bg-[#F1EAE0]"}`} data-testid={`assignee-option-${m.id}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const ids = new Set(form.assignee_ids || []);
                            if (e.target.checked) ids.add(m.id); else ids.delete(m.id);
                            set("assignee_ids", Array.from(ids));
                          }}
                          className="w-4 h-4 accent-[#3B2B23]"
                        />
                        <span className="flex-1 text-[#1A1412]">{m.name}</span>
                        <span className="text-[9px] uppercase tracking-widest text-[#8B5E3C]">{m.role === "admin" ? "Gestor" : (m.profession || "Equipa")}</span>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="text-[10px] text-[#8B5E3C] mt-1">
                {(form.assignee_ids || []).length} responsável{(form.assignee_ids || []).length === 1 ? "" : "eis"} selecionado{(form.assignee_ids || []).length === 1 ? "" : "s"}
              </div>
            </div>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">Estado</Label>
            <select
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
              className="w-full mt-1 h-11 px-3 rounded-md border border-[#EAE3DB] bg-white text-sm text-[#1A1412] focus:outline-none focus:ring-2 focus:ring-[#8B5E3C]"
              data-testid="task-status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {!isEditing && (
            <div className="rounded-md border border-[#EAE3DB] bg-white/60 p-3 space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">Repetir</Label>
                <select
                  value={form.recurrence}
                  onChange={(e) => set("recurrence", e.target.value)}
                  className="w-full mt-1 h-11 px-3 rounded-md border border-[#EAE3DB] bg-white text-sm text-[#1A1412] focus:outline-none focus:ring-2 focus:ring-[#8B5E3C]"
                  data-testid="task-recurrence-select"
                >
                  <option value="none">Sem repetição</option>
                  <option value="daily">Diariamente</option>
                  <option value="weekly">Semanalmente</option>
                  <option value="monthly">Mensalmente</option>
                  <option value="custom">Personalizado (de X em X dias)</option>
                </select>
              </div>
              {form.recurrence !== "none" && (
                <div className="grid grid-cols-2 gap-3">
                  {form.recurrence === "custom" && (
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">A cada (dias)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={form.recurrence_interval}
                        onChange={(e) => set("recurrence_interval", e.target.value)}
                        className="mt-1 h-11 bg-white"
                        data-testid="task-recurrence-interval"
                      />
                    </div>
                  )}
                  <div className={form.recurrence === "custom" ? "" : "col-span-2"}>
                    <Label className="text-[10px] uppercase tracking-widest text-[#5C4A43]">Repetir até</Label>
                    <Input
                      type="date"
                      value={form.recurrence_until}
                      onChange={(e) => set("recurrence_until", e.target.value)}
                      className="mt-1 h-11 bg-white"
                      data-testid="task-recurrence-until"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {isEditing && (
          <div className="mt-5 pt-5 border-t border-[#EAE3DB]" data-testid="task-attachments-section">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-[#8B5E3C]">
                Anexos {attachments.length ? `(${attachments.length})` : ""}
              </div>
              <label className="cursor-pointer text-xs uppercase tracking-widest text-[#3B2B23] hover:text-[#1A1412] flex items-center gap-1.5" data-testid="attachment-upload-label">
                <Paperclip className="w-3.5 h-3.5" /> {uploading ? "A carregar…" : "Anexar"}
                <input
                  type="file"
                  className="hidden"
                  onChange={uploadAttachment}
                  disabled={uploading}
                  data-testid="attachment-file-input"
                />
              </label>
            </div>
            {attachments.length === 0 ? (
              <div className="text-xs text-[#8B5E3C] italic text-center py-3">
                Sem anexos. Podes adicionar imagens, vídeos ou documentos (até 25MB).
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {attachments.map((a) => {
                  const isImg = a.kind === "image";
                  const isVid = a.kind === "video";
                  const Icon = isImg ? ImageIcon : isVid ? Film : FileText;
                  const canDelete = a.uploaded_by === user?.id || isAdmin;
                  return (
                    <div key={a.id} className="relative group bg-white border border-[#EAE3DB] rounded-lg overflow-hidden" data-testid={`attachment-${a.id}`}>
                      {isImg ? (
                        <a href={a.url} target="_blank" rel="noreferrer" className="block aspect-video bg-[#EAE3DB] overflow-hidden">
                          <img src={a.url} alt={a.filename} className="w-full h-full object-cover" />
                        </a>
                      ) : (
                        <a href={a.url} target="_blank" rel="noreferrer" className="flex items-center justify-center aspect-video bg-[#F1EAE0]">
                          <Icon className="w-8 h-8 text-[#8B5E3C]" />
                        </a>
                      )}
                      <div className="p-2 flex items-center gap-1.5">
                        <Icon className="w-3.5 h-3.5 text-[#8B5E3C] flex-shrink-0" />
                        <a href={a.url} target="_blank" rel="noreferrer" title={a.filename} className="text-[11px] text-[#1A1412] truncate flex-1 hover:underline">
                          {a.filename}
                        </a>
                        <a href={a.url} download={a.filename} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-[#EAE3DB] text-[#3B2B23]" title="Descarregar">
                          <Download className="w-3 h-3" />
                        </a>
                        {canDelete && (
                          <button
                            onClick={() => removeAttachment(a)}
                            className="p-1 rounded hover:bg-[#FBE6E4] text-[#8A2A22]"
                            title="Eliminar anexo"
                            data-testid={`delete-attachment-${a.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {isEditing && (
          <div className="mt-5 pt-5 border-t border-[#EAE3DB]" data-testid="task-comments-section">
            <div className="text-[10px] uppercase tracking-widest text-[#8B5E3C] mb-3">
              Comentários da equipa {comments.length ? `(${comments.length})` : ""}
            </div>
            <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
              {comments.length === 0 && (
                <div className="text-xs text-[#8B5E3C] italic text-center py-3">
                  Ainda não há comentários nesta tarefa.
                </div>
              )}
              {comments.map((c) => {
                const mine = c.user_id === user?.id;
                const canDelete = mine || isAdmin;
                const when = new Date(c.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
                return (
                  <div
                    key={c.id}
                    className={`rounded-lg p-2.5 ${mine ? "bg-[#EAE3DB]" : "bg-white border border-[#EAE3DB]"}`}
                    data-testid={`comment-${c.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] uppercase tracking-widest text-[#5C4A43] flex items-center gap-2">
                        <span className="font-bold">{c.user_name}</span>
                        {c.user_role === "admin" && (
                          <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[#3B2B23] text-[#F9F6F0]">Gestor</span>
                        )}
                        <span className="text-[#8B5E3C]">{when}</span>
                      </div>
                      {canDelete && (
                        <button
                          onClick={() => removeComment(c)}
                          className="p-1 rounded-full hover:bg-[#FBE6E4] text-[#8A2A22]"
                          aria-label="Eliminar comentário"
                          data-testid={`delete-comment-${c.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-[#1A1412] mt-1 whitespace-pre-wrap leading-relaxed">
                      {c.content}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-end gap-2 relative">
              <div className="flex-1 relative">
                <Textarea
                  value={newComment}
                  onChange={onCommentChange}
                  onKeyDown={(e) => {
                    if (mentionState.open && e.key === "Escape") { setMentionState({ open: false, query: "", start: 0 }); return; }
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); postComment(); }
                  }}
                  placeholder="Escreve um comentário… (usa @ para mencionar; Ctrl/⌘ + Enter para enviar)"
                  className="bg-white min-h-[54px] max-h-32 w-full"
                  data-testid="new-comment-input"
                />
                {mentionState.open && (() => {
                  const matches = team.filter((m) =>
                    m.id !== user?.id &&
                    (m.name || "").toLowerCase().includes(mentionState.query.toLowerCase())
                  ).slice(0, 6);
                  if (matches.length === 0) return null;
                  return (
                    <div className="absolute bottom-full left-0 mb-1 w-full max-w-xs bg-white border border-[#EAE3DB] rounded-lg shadow-lg overflow-hidden z-50" data-testid="mention-dropdown">
                      {matches.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => pickMention(m)}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-[#EAE3DB] flex items-center justify-between gap-2 border-b border-[#EAE3DB] last:border-b-0"
                          data-testid={`mention-option-${m.id}`}
                        >
                          <span className="font-bold text-[#1A1412] truncate">{m.name}</span>
                          <span className="text-[9px] uppercase tracking-widest text-[#8B5E3C]">{m.role === "admin" ? "Gestor" : (m.profession || "Equipa")}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <Button
                onClick={postComment}
                disabled={postingComment || !newComment.trim()}
                className="rounded-full h-11 w-11 p-0 bg-[#3B2B23] text-[#F9F6F0] hover:bg-[#2A1E18] flex-shrink-0"
                data-testid="post-comment-button"
                aria-label="Publicar comentário"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full h-11 px-6 text-xs uppercase tracking-widest border-[#3B2B23] text-[#3B2B23]"
            data-testid="task-cancel-button"
          >
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-full h-11 px-6 text-xs uppercase tracking-widest bg-[#3B2B23] text-[#F9F6F0] hover:bg-[#2A1E18]"
            data-testid="task-save-button"
          >
            {saving ? "A guardar…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
